import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { useTheme } from '@mui/material'
import { Breadcrumb, ThemeProvider } from '@center/ui-kit'

import { buildClusterIcon } from './helpers'
import { ModalContainer } from './styles'
import MapType from '../Controls/MapType'
import Zoom from '../Controls/Zoom'
import Legends from '../Controls/Legends'
import Expand from '../Controls/Expand'
import Geolocation, { Search } from '../Controls/Geolocation'

export const useControls = ({
  google,
  cluster,
  isExpanded,
  onToggleExpand,
  showExpandButton = true,
  onGeolocationChange = null,
  onGeolocationReset = null
}) => {
  const [controlsRefs, setControlsRefs] = useState({})
  
  const initialize = () => ({
    zoom: document.createElement('div'),
    legend: document.createElement('div'),
    breadcrumb: document.createElement('div'),
    modal: document.createElement('div'),
    maptype: document.createElement('div'),
    expand: document.createElement('div'),
    geolocation: document.createElement('div'),
    search: document.createElement('div'),
  })
  
  const render = ({ map, activeEquipment, isProjectHomeView = false }) => {
    const controls = initialize()

    const zoomRoot = createRoot(controls.zoom)
    zoomRoot.render(
      <ThemeProvider>
        <Zoom 
          mapsView="global"
          onZoomIn={() => map.setZoom(map.getZoom() + 1)}
          onZoomOut={() => map.setZoom(map.getZoom() - 1)}
        />
      </ThemeProvider>
    )

    const geolocationRoot = createRoot(controls.geolocation)
    geolocationRoot.render(
      <ThemeProvider>
        <Geolocation onClick={onGeolocationReset} />
      </ThemeProvider>
    )

    const searchRoot = createRoot(controls.search)
    searchRoot.render(
      <ThemeProvider>
        <Search onChange={onGeolocationChange} />
      </ThemeProvider>
    )

    const legendRoot = createRoot(controls.legend)
    legendRoot.render(
      <ThemeProvider>
        <Legends mapsView="global"/>
      </ThemeProvider>
    )

    const breadcrumbRoot = createRoot(controls.breadcrumb)
    breadcrumbRoot.render(
      <ThemeProvider>
        <Breadcrumb onExit={() => {
          if(cluster?.markers?.length) {
            const bounds = new google.maps.LatLngBounds()
            cluster.markers.forEach(({ position }) => bounds.extend(position))
            map.fitBounds(bounds)
          }
        }} />
      </ThemeProvider>
    )

    const modalRoot = createRoot(controls.modal)
    modalRoot.render(
      <ThemeProvider>
        <ModalContainer>
          {activeEquipment?.content}
        </ModalContainer>
      </ThemeProvider>
    )

    const maptypeRoot = createRoot(controls.maptype)
    maptypeRoot.render(
      <ThemeProvider>
        <MapType
          mapsView="global"
          isProjectHomeView={isProjectHomeView}
          handleRoadmapClick={() => map.setMapTypeId('roadmap')}
          handleSatelliteClick={() => {
            // display an error instead of the satellite view when the zoom level is too low
            if (map.zoom >= 17 ) {
              map.setZoom(17)
            }
            map.setMapTypeId('satellite')
          }}
        />
      </ThemeProvider>
    )

    const expandRoot = createRoot(controls.expand)
    expandRoot.render(
      <ThemeProvider>
        <Expand
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
          showExpandButton={showExpandButton}
        />
      </ThemeProvider>
    )

    setControlsRefs({
      zoomRoot,
      legendRoot,
      breadcrumbRoot,
      modalRoot,
      maptypeRoot,
      expandRoot,
      geolocationRoot,
      searchRoot
    })

    return controls
  }

  return { render, controlsRefs }
}

const DEFAULT_ALGORITHM_OPTIONS = {
  minPoints: 5,
  radius: 60,
  maxZoom: 16,
  minZoom: 0,
  extent: 512,
  nodeSize: 64
}

const useMaps = ({
  google,
  mapOptions,
  hasZoom = true,
  algorithmOption = {},
  activeEquipment,
  infoWindow,
  isExpanded = false,
  onToggleExpand = () => {},
  showExpandButton = true,
  isProjectHomeView = false,
  hideLegends = false,
  onGeolocationChange = null,
  onGeolocationReset = null,
}) => {
  const [map, setMap] = useState(null)
  const [cluster, setCluster] = useState(null)
  const theme = useTheme()
  const { render: renderControls } = useControls({
    google,
    cluster,
    isExpanded,
    onToggleExpand,
    showExpandButton,
    onGeolocationChange,
    onGeolocationReset
  })

  const initialize = () => {
    if (!map) return
    
    const controls = renderControls({ map, activeEquipment, isProjectHomeView })
    map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].clear()
    if (hasZoom) {
      map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(controls.zoom)
    }
    if (!hideLegends) {
      map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(controls.legend)
    }
    
    map.controls[google.maps.ControlPosition.RIGHT_TOP].clear()
    map.controls[google.maps.ControlPosition.RIGHT_TOP].push(controls.maptype)
    if (onGeolocationReset) {
      map.controls[google.maps.ControlPosition.RIGHT_TOP].push(controls.geolocation)
    }

    map.controls[google.maps.ControlPosition.LEFT_TOP].clear()
    if (showExpandButton) {
      map.controls[google.maps.ControlPosition.LEFT_TOP].push(controls.expand)
    }
    if (onGeolocationChange) {
      map.controls[google.maps.ControlPosition.LEFT_TOP].push(controls.search)
    }
    
    map.controls[google.maps.ControlPosition.LEFT_BOTTOM].clear()
    map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(controls.breadcrumb)
    map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(controls.modal)
  }

  const updateActiveEquipment = () => {
    if (!map) return
    
    const controls = renderControls({ map, activeEquipment, isProjectHomeView })
    if (map.controls[google.maps.ControlPosition.LEFT_BOTTOM].length > 1) {
      map.controls[google.maps.ControlPosition.LEFT_BOTTOM].pop()
    }
    map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(controls.modal)
  }

  useEffect(() => {
    if (google && !map) {
      const element = document.getElementById('mapViewGlobal')
      if (element) {
        const newMap = new google.maps.Map(element, mapOptions)
        const clusterOptions = { ...DEFAULT_ALGORITHM_OPTIONS, ...algorithmOption }
        
        const markersCluster = new MarkerClusterer({
          algorithm: new SuperClusterAlgorithm(clusterOptions),
          renderer: {
            render: ({ count, position }) => {
              const color = theme.palette.grey.main
              if (count === 1) {
                return new google.maps.Marker({ position })
              }
              return new google.maps.Marker({
                position,
                icon: buildClusterIcon(google, color),
                label: {
                  text: String(count),
                  color: 'white',
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                },
                zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
              })
            }
          },
          markers: [],
          map: newMap
        })
        
        setCluster(markersCluster)
        setMap(newMap)
      }
    }
  }, [google])

  useEffect(() => {
    if (map) {
      initialize()
    }
  }, [map, isExpanded, showExpandButton])

  useEffect(() => {
    if (map && activeEquipment) {
      updateActiveEquipment()
    }
  }, [map, activeEquipment])

  return {
    initialize,
    updateActiveEquipment,
    ready: Boolean(cluster && map),
    cluster,
    infoWindow,
    map,
    addMarkersOnMap: (markers) => markers?.forEach(marker => marker.setMap(map)),
    removeMarkersFromMap: (markers) => markers?.forEach(marker => marker.setMap(null)),
  }
}

export default useMaps